import {Injectable} from '@angular/core';
import {EMPTY, merge, Observable, of, throwError} from 'rxjs';
import {catchError, defaultIfEmpty, map, mapTo, switchMap} from 'rxjs/operators';
import {isApiErrorArray} from '../../../common/api-error-array';
import {ListResponse, ResponseError} from '../../../common/api-helpers';
import {toNever} from '../../../common/rxjs/to-never';
import {CommonListFilter} from '../../../models/filters/common-list-filter';
import {SecurityKey} from '../../../models/security-key';
import {UserService} from '../../../services/user.service';
import {SecurityAutocompleteService} from '../../security-autocomplete';
import {getSecurityKeyFromFilterCriteria} from '../security-filter-criterion-utils';
import {AsecurityCreationError} from './asecurity-errors';
import {AspreaderSecurity, AspreaderSecurityConfig, parseAsecurityConfig} from './aspreader-security';
import {
    appendAsecurityVersionToName,
    AsecurityVersionInfo,
    ASPREADER_SECURITY_FIRST_VER,
    buildSymbolFromAsecurityVersionInfo,
    chooseLastAsecurityVersionBySymbol,
    chooseLastAsecurityVersionByVerInfo,
    isAsecurityConfigEqual,
    isAsecurityVersionInfoSameInSeries,
    parseAsecurityVersionFromSymbol,
    removeAsecurityVersionFromName
} from './aspreader-security-version';
import {AspreaderSecurityService} from './aspreader-security.service';

interface AsecurityVerFilterPredicateExtResult {
    isMatch: boolean;
    doStopIterations: boolean;
}

type AsecurityVerFilterPredicate = (
    security: AspreaderSecurity,
    versionInfo: AsecurityVersionInfo
) => AsecurityVerFilterPredicateExtResult | boolean;

@Injectable()
export class AspreaderSecurityVersionService {

    constructor(
        private aspreaderSecurityService: AspreaderSecurityService,
        private userService: UserService
    ) {}

    public getNextVersionNumberBySymbol(symbol: string): Observable<number> {
        return this.aspreaderSecurityService.fetchFilteredAspreaderSecurities().pipe(
            map(asecurities => {
                const lastVersion = chooseLastAsecurityVersionBySymbol(asecurities, symbol);

                return lastVersion.lastVerNum === undefined
                    ? ASPREADER_SECURITY_FIRST_VER
                    : lastVersion.lastVerNum + 1;
            })
        );
    }

    public getNextVersionNumberByName(name: string, userId?: number): Observable<number> {
        userId = userId || (this.userService.user && this.userService.user.id);

        const fakeSymbol = buildSymbolFromAsecurityVersionInfo({
            name,
            versionNumber: ASPREADER_SECURITY_FIRST_VER,
            userId
        });

        return this.getNextVersionNumberBySymbol(fakeSymbol);
    }

    public getLastVersionSecurityBySymbol(symbol: string): Observable<AspreaderSecurity> {
        return this.getLastVersionSecurityByVerInfo(parseAsecurityVersionFromSymbol(symbol));
    }

    public getLastVersionSecurityByVerInfo(
        verInfo: Pick<AsecurityVersionInfo, 'name' | 'userId'>,
        deactivatePrevVersions: boolean = false
    ): Observable<AspreaderSecurity> {
        return this.aspreaderSecurityService.fetchFilteredAspreaderSecurities().pipe(
            switchMap(asecurities => {
                const lastVersion = chooseLastAsecurityVersionByVerInfo(asecurities, verInfo);

                return deactivatePrevVersions
                    ? this.deactivatePrevVersions(asecurities, lastVersion.lastVerInfo).pipe(
                        defaultIfEmpty(undefined), // deactivatePrevVersions doesn't emit any value, it's just completed.
                        mapTo(lastVersion.lastVerSecurity)
                    )
                    : of(lastVersion.lastVerSecurity);
            })
        );
    }

    public getFilteredLastVersionSecurities(nameFilter?: string): Observable<AspreaderSecurity[]> {
        nameFilter = nameFilter && nameFilter.trim().toLocaleLowerCase();

        const filterPredicate: AsecurityVerFilterPredicate | undefined = nameFilter
            ? (security, versionInfo) =>
                SecurityAutocompleteService.isSubStrIncludedInStrByWords(
                    versionInfo.name,
                    nameFilter
                )
            : undefined;

        return this.aspreaderSecurityService.fetchFilteredAspreaderSecurities().pipe(
            map(rawList => this.chooseLastVersionSecurities(rawList, filterPredicate))
        );
    }

    public isAspreaderSecurityRequest(listFilter: CommonListFilter): boolean {
        return this.aspreaderSecurityService.isAspreaderSecurityRequest(listFilter);
    }

    public emulateSecurityRestRequest(listFilter: CommonListFilter): Observable<ListResponse<AspreaderSecurity>> {
        const securityKey = getSecurityKeyFromFilterCriteria(listFilter.filters);

        return this.getLastVersionSecurityByKey(securityKey).pipe(
            map<AspreaderSecurity | undefined, ListResponse<AspreaderSecurity>>(asecurity => ({
                data:     asecurity ? [asecurity] : [],
                listInfo: {
                    pageSize:  1,
                    pageIndex: 0,
                    pageCount: 1
                }
            }))
        );
    }

    public isAsecurityConfigSameAsInLastVer(
        asecurityConfig: AspreaderSecurityConfig,
        userId?: number
    ): Observable<boolean> {
        userId = userId || (this.userService.user && this.userService.user.id);

        const noVerSecurityConfig: AspreaderSecurityConfig = {
            ...asecurityConfig,
            name: removeAsecurityVersionFromName(asecurityConfig.name)
        };

        return this.getLastVersionSecurityByVerInfo({
            name: asecurityConfig.name,
            userId
        }).pipe(
            map(lastVerSecurity => {
                const lastVerSecurityConfig = parseAsecurityConfig(lastVerSecurity);
                const lastNoVerSecurityConfig: AspreaderSecurityConfig = {
                    ...lastVerSecurityConfig,
                    name: removeAsecurityVersionFromName(lastVerSecurityConfig.name)
                };

                return isAsecurityConfigEqual(lastNoVerSecurityConfig, noVerSecurityConfig);
            })
        );
    }

    /**
     * Tries to create Autospreader security with incremented versions, if the name is already used.
     */
    public createAsecurityWithVersionAttempts(
        asecurityConfig: AspreaderSecurityConfig,
        version: number
    ): Observable<unknown> {
        const MAX_VERSION_ATTEMPTS: number = 50;
        let verAttemptNumber: number = 0;

        const doTry = (): Observable<unknown> =>
            this.aspreaderSecurityService.createAspreaderSecurity({
                ...asecurityConfig,
                name: appendAsecurityVersionToName(asecurityConfig.name, version + verAttemptNumber)
            }).pipe(
                catchError((savingError: ResponseError | unknown) => {
                    let result$: Observable<any>;

                    if (savingError && isApiErrorArray((savingError as ResponseError).message)) {
                        const errorCodes = (savingError as ResponseError).message as unknown as string[];

                        if (errorCodes.length === 1 && errorCodes[0] === AsecurityCreationError.NAME_EXISTS) {
                            verAttemptNumber++;

                            if (verAttemptNumber <= MAX_VERSION_ATTEMPTS) {
                                result$ = doTry();
                            }
                        }
                    }

                    return result$ || throwError(savingError);
                })
            );

        return doTry();
    }

    private chooseLastVersionSecurities(
        allAsecurities: AspreaderSecurity[],
        filterPredicate?: AsecurityVerFilterPredicate
    ): AspreaderSecurity[] {
        interface IterationRec {
            security: AspreaderSecurity;
            versionInfo?: AsecurityVersionInfo;
        }

        interface LastVersionItRecInfo {
            lastVerNum: number | undefined;
            lastVerItRec: IterationRec;
            newItList: IterationRec[];
        }

        const getOrCreateVerInfoFromItRec = (itRec: IterationRec): AsecurityVersionInfo =>
            itRec.versionInfo = itRec.versionInfo
                || parseAsecurityVersionFromSymbol(itRec.security.symbol);

        const resultSecuritiesList: AspreaderSecurity[] = [];
        let itList: IterationRec[] = allAsecurities.map<IterationRec>(security => ({security}));

        while (itList.length) {
            const firstItRec = itList[0];
            const firstItRecVerInfo = getOrCreateVerInfoFromItRec(firstItRec);
            let doStopIterations: boolean = false;

            const {lastVerItRec, newItList} = itList
                .reduce<LastVersionItRecInfo>(
                    (acc, itRec) => {
                        const itRecVerInfo = getOrCreateVerInfoFromItRec(itRec);

                        if (isAsecurityVersionInfoSameInSeries(itRecVerInfo, firstItRecVerInfo)) {
                            if (!acc.lastVerItRec || acc.lastVerNum < itRecVerInfo.versionNumber) {
                                acc.lastVerNum = itRecVerInfo.versionNumber;
                                acc.lastVerItRec = itRec;
                            }
                        } else {
                            acc.newItList.push(itRec);
                        }

                        return acc;
                    },
                    {
                        lastVerNum:   firstItRecVerInfo.versionNumber,
                        lastVerItRec: firstItRec,
                        newItList:    []
                    }
                );

            if (filterPredicate) {
                const filterPredicateResult = filterPredicate(
                    lastVerItRec.security,
                    getOrCreateVerInfoFromItRec(lastVerItRec)
                );

                if (filterPredicateResult) {
                    if (
                        filterPredicateResult === true
                        || (filterPredicateResult as AsecurityVerFilterPredicateExtResult).isMatch
                    ) {
                        resultSecuritiesList.push(lastVerItRec.security);
                    }

                    if ((filterPredicateResult as AsecurityVerFilterPredicateExtResult).doStopIterations) {
                        doStopIterations = true;
                    }
                }
            } else {
                resultSecuritiesList.push(lastVerItRec.security);
            }

            itList = newItList;

            if (doStopIterations) {
                break;
            }
        }

        return resultSecuritiesList;
    }

    private getLastVersionSecurityByKey(securityKey: SecurityKey): Observable<AspreaderSecurity> {
        return this.aspreaderSecurityService.fetchFilteredAspreaderSecurities().pipe(
            map(asecurities => {
                const lastVersion = chooseLastAsecurityVersionBySymbol(asecurities, securityKey.symbol);

                return lastVersion.lastVerSecurity;
            })
        );
    }

    private deactivatePrevVersions(
        allAsecurities: AspreaderSecurity[],
        lastVerInfo: AsecurityVersionInfo
    ): Observable<never> {
        const requests = allAsecurities.reduce<Array<Observable<unknown>>>(
            (acc, asecurity) => {
                const verInfo = parseAsecurityVersionFromSymbol(asecurity.symbol);

                if (
                    verInfo.name === lastVerInfo.name
                    && verInfo.userId === lastVerInfo.userId
                    && verInfo.versionNumber < lastVerInfo.versionNumber
                ) {
                    acc.push(
                        this.aspreaderSecurityService.deactivateAspreaderSecurity(asecurity.id)
                    );
                }

                return acc;
            },
            []
        );

        return requests.length
            ? merge(...requests).pipe(
                toNever()
            )
            : EMPTY;
    }

}
